import { KpiStrip, MetricCard, Panel, SectionHeader, ErpTable, Th, Td, EmptyRow } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { totalRollup } from './api'
import { ElectionPicker } from './ElectionPicker'
import { useElection } from './useElection'
import { nationalOr } from './units'

/**
 * Overview — election picker, KPI strip (coverage, coordinator fill, pledge capture,
 * transport gaps, spend), and the day's state.
 *
 * A caller without `turnout:read` sees Forbidden. The KPIs are whatever the rollup
 * returned — no invented metrics.
 */
export function StationMatesOverviewPage() {
  const canRead = useCan('turnout:read')
  const selection = useElection()
  const { electionId, election, elections } = selection
  // The rollup is scoped to the election's own unit — national when it has none.
  const data = useLoad(
    () => (electionId && election
      /*
         The unit is the election's SCOPE unit. A national election has none, and the
         national rollup is the COUNTRY unit — not the election id, which is what this
         used to fall back to and is why the request carried the same uuid twice and
         could never match a row.
      */
      ? nationalOr(election.scopeUnitId).then((unitId) =>
          unitId ? totalRollup(electionId, unitId) : null)
      : Promise.resolve(null)),
    [electionId, election?.scopeUnitId])

  if (!canRead) return <Forbidden what="Know Your Polling Station Mate" />

  const rollup = data.data as unknown as Record<string, unknown> | null | undefined

  const pct = (a: number, b: number) => b === 0 ? '—' : `${Math.round(100 * a / b)}%`
  const fmt = (n: unknown) => typeof n === 'number' ? n.toLocaleString() : '—'

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Know Your Polling Station Mate"
        description="Cell coverage, pledge pipeline and live turnout — all from one place."
        right={<ElectionPicker selection={selection} />}
      />

      <ErrorStrip message={selection.error ?? data.error} />

      {(selection.loading || data.loading) && !rollup
        ? <LoadingBlock label="Loading overview…" /> : (
        <>
          <KpiStrip>
            <MetricCard
              label="Cells active"
              value={rollup ? pct(rollup.cellsActive as number, rollup.cellsTotal as number) : '—'}
              sub={rollup ? `${fmt(rollup.cellsActive)} of ${fmt(rollup.cellsTotal)}` : 'no data'}
            />
            <MetricCard
              label="Coordinator fill"
              value={rollup ? pct(rollup.cellsWithCoordinator as number, rollup.cellsTotal as number) : '—'}
              sub="cells with a named coordinator"
              accent={!!rollup && (rollup.cellsWithCoordinator as number) < (rollup.cellsTotal as number)}
            />
            <MetricCard
              label="Pledges committed"
              value={rollup ? pct(rollup.pledgesCommitted as number, rollup.membersActive as number) : '—'}
              sub={rollup ? `${fmt(rollup.pledgesCommitted)} of ${fmt(rollup.membersActive)} members` : 'no data'}
            />
            <MetricCard
              label="Transport gaps"
              value={fmt(rollup?.transportGaps)}
              sub="open requests with no match"
              accent={!!rollup && (rollup.transportGaps as number) > 0}
            />
            <MetricCard
              label="Voted"
              value={rollup ? pct(rollup.votedCount as number, rollup.registeredCount as number) : '—'}
              sub={rollup ? `${fmt(rollup.votedCount)} of ${fmt(rollup.registeredCount)} registered` : 'no data'}
            />
          </KpiStrip>

          <Panel title="Elections" description="Active and upcoming elections. Click an election to scope the station and cell views.">
            <ErpTable head={(
              <>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Status</Th>
                <Th>Polling date</Th>
              </>
            )}>
              {elections.length === 0 ? (
                <EmptyRow colSpan={4} message="No elections yet. Create one from the Elections tab." />
              ) : elections.map((e) => (
                <tr key={e.id}
                  className={`hover:bg-muted/40 cursor-pointer ${e.id === electionId ? 'bg-muted/30' : ''}`}
                  onClick={() => selection.select(e.id)}>
                  <Td className="font-mono text-erp-xs">{e.code}</Td>
                  <Td>{e.name}</Td>
                  <Td className="text-muted-foreground">{e.statusCode}</Td>
                  <Td className="text-muted-foreground">{e.pollingDate}</Td>
                </tr>
              ))}
            </ErpTable>
          </Panel>
        </>
      )}
    </div>
  )
}
